import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { http } from "@/utils/http";
import { useToasts } from "@/components/ui";

import { Thread } from "../contexts/PageManagerContext";

interface UseChatThreadsReturn {
  isLoading: boolean;
  threads: Thread[];
  clearThread: (threadId: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  refreshThreads: () => void;
  renameThread: (threadId: string, newTitle: string) => Promise<void>;
}

export const useChatThreads = (): UseChatThreadsReturn => {
  const queryClient = useQueryClient();
  const { error: errorToast, success: successToast } = useToasts();

  // Fetch threads using TanStack React Query
  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["chatbot-threads"],
    queryFn: async (): Promise<Thread[]> => {
      const response = await http.get<Thread[]>("/api/chatbot/threads");
      return response.data || [];
    },
  });

  // Delete thread mutation
  const deleteThreadMutation = useMutation({
    mutationFn: async (threadId: string): Promise<void> => {
      await http.delete(`/api/chatbot/threads/${threadId}`);
    },
    onError: (error) => {
      errorToast(
        `Failed to delete thread: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    },
    onSuccess: () => {
      // Invalidate and refetch threads
      queryClient.invalidateQueries({ queryKey: ["chatbot-threads"] });
      successToast("Thread deleted successfully");
    },
  });

  // Rename thread mutation
  const renameThreadMutation = useMutation({
    mutationFn: async ({ newTitle, threadId }: { newTitle: string; threadId: string; }): Promise<void> => {
      await http.patch(`/api/chatbot/threads/${threadId}`, { title: newTitle });
    },
    onError: (error) => {
      errorToast(
        `Failed to rename thread: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-threads"] });
      successToast("Thread renamed successfully");
    },
  });

  // Clear thread mutation
  const clearThreadMutation = useMutation({
    mutationFn: async (threadId: string): Promise<void> => {
      await http.delete(`/api/chatbot/threads/${threadId}/messages`);
    },
    onError: (error) => {
      errorToast(
        `Failed to clear thread: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-threads"] });
      queryClient.invalidateQueries({ queryKey: ["chatbot-messages"] });
      successToast("Thread cleared successfully");
    },
  });

  const deleteThread = async (threadId: string): Promise<void> => {
    return deleteThreadMutation.mutateAsync(threadId);
  };

  const renameThread = async (threadId: string, newTitle: string): Promise<void> => {
    return renameThreadMutation.mutateAsync({ newTitle, threadId });
  };

  const clearThread = async (threadId: string): Promise<void> => {
    return clearThreadMutation.mutateAsync(threadId);
  };

  const refreshThreads = (): void => {
    queryClient.invalidateQueries({ queryKey: ["chatbot-threads"] });
  };

  return {
    clearThread,
    deleteThread,
    isLoading,
    refreshThreads,
    renameThread,
    threads,
  };
};
